import { DeviceRegistry } from './deviceRegistry';
import { ChangeDecision, NotifyReason, SnapshotDiff, SwitchCooldown, decideChange, diffSnapshots } from './devicePolicy';
import { PlatformQuirks } from './platformQuirks';
import { DEVICE_CHANGE_DEBOUNCE_MS, DeviceSnapshot, LoggerBridge, UNPAIRED_INPUT_TTL_MS, emptyDeviceSnapshot, isMobilePlatform, isRealDeviceId } from './shared';

// Where the watcher sends its decisions: the C# component that owns device switching.
export interface DeviceChangeSink {
    setRef(ref: any): void;
    onDeviceChanged(fallbackDeviceId: string | null, reason: NotifyReason): Promise<void>;
}

// The microphone the watcher protects.
export interface ActiveMicState {
    readonly currentMicId: string | null;
    readonly isActive: boolean;
}

// The output the engine currently plays to.
export interface OutputSinkState {
    readonly sinkId: string | null;
}

//#region Device watcher
// Owns the devicechange subscription: debounces event bursts, diffs enumeration snapshots,
// asks the policy for a decision, notifies C#, and runs internal routing repairs.
export class DeviceWatcher {
    private handler: EventListener | null = null;
    private debounceTimer: number | null = null;
    private baseline: DeviceSnapshot | null = null;
    private recentUnpairedInputs: Map<string, number> = new Map();

    constructor(
        private readonly registry: DeviceRegistry,
        private readonly bridge: DeviceChangeSink,
        private readonly logger: LoggerBridge,
        private readonly quirks: PlatformQuirks,
        private readonly mic: ActiveMicState,
        private readonly cooldown: SwitchCooldown,
        private readonly output: OutputSinkState,
        private readonly repairOutput: () => Promise<unknown>) { }

    async register(componentRef: any): Promise<void> {
        this.unregister();
        this.bridge.setRef(componentRef);
        try {
            this.baseline = await this.registry.enumerate();
        } catch {
            this.baseline = emptyDeviceSnapshot();
        }
        // Debounce: coalesces rapid bursts (one event per device added/removed) into a single pass.
        this.handler = (() => {
            if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
            this.debounceTimer = window.setTimeout(() => {
                this.debounceTimer = null;
                void this.handleDeviceChange();
            }, DEVICE_CHANGE_DEBOUNCE_MS);
        }) as EventListener;
        try {
            navigator.mediaDevices.addEventListener('devicechange', this.handler);
            await this.logger.info('Device change listener registered');
        } catch (e) {
            await this.logger.warning('Failed to register devicechange listener', { error: String(e) });
        }
    }

    unregister(): void {
        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.handler) {
            try {
                navigator.mediaDevices.removeEventListener('devicechange', this.handler);
            } catch { }
            this.handler = null;
        }
        this.baseline = null;
        this.recentUnpairedInputs.clear();
    }

    // Runs the same debounced re-evaluation a devicechange event would trigger. Used by the
    // mic pipeline when the active track ends unexpectedly (permission revocation and some
    // device removals end the track without a reliable devicechange event).
    requestRecheck(): void {
        if (!this.handler) return; // not registered — hot-plug handling is inactive
        if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
        this.debounceTimer = window.setTimeout(() => {
            this.debounceTimer = null;
            void this.handleDeviceChange();
        }, DEVICE_CHANGE_DEBOUNCE_MS);
    }

    private async handleDeviceChange(): Promise<void> {
        try {
            const snapshot = await this.registry.enumerate();
            const prev = this.baseline ?? emptyDeviceSnapshot();
            const diff = diffSnapshots(prev, snapshot, this.mic.currentMicId, this.mic.isActive);
            const decision = decideChange(diff, {
                snapshot,
                currentMicId: this.mic.currentMicId,
                currentSinkId: this.output.sinkId,
                micActive: this.mic.isActive,
                isMobile: isMobilePlatform(),
                cooldownActive: this.cooldown.isActive,
                msSinceExplicitSwitch: this.cooldown.msSinceExplicitSwitch,
                recentUnpairedInputs: this.recentUnpairedInputs,
                now: Date.now(),
                quirks: this.quirks
            });

            const logPayload = {
                reason: decision.reason,
                rule: decision.rule,
                fallbackDeviceId: decision.fallbackDeviceId,
                ...decision.logProps
            };
            if (decision.reason === 'mic-disconnected') {
                await this.logger.warning('Device change decision', logPayload);
            } else {
                await this.logger.info('Device change decision', logPayload);
            }

            for (const action of decision.internalActions) {
                try {
                    if (action === 'repair-output') {
                        await this.repairOutput();
                    }
                } catch (e) {
                    await this.logger.warning(`Internal action '${action}' failed`, { error: String(e) });
                }
            }

            await this.bridge.onDeviceChanged(decision.fallbackDeviceId, decision.reason);
            this.updateTracking(diff, snapshot, decision);
        } catch (e) {
            await this.logger.warning('handleDeviceChange error', { error: String(e) });
        }
    }

    private updateTracking(diff: SnapshotDiff, snapshot: DeviceSnapshot, decision: ChangeDecision): void {
        this.baseline = snapshot;

        // Drop unpaired-input candidates that were removed or have aged out.
        const currentIds = new Set(snapshot.inputs.filter(d => isRealDeviceId(d.deviceId)).map(d => d.deviceId));
        const now = Date.now();
        for (const [id, firstSeen] of [...this.recentUnpairedInputs]) {
            if (!currentIds.has(id) || now - firstSeen > UNPAIRED_INPUT_TTL_MS) {
                this.recentUnpairedInputs.delete(id);
            }
        }

        // New inputs that were not auto-switched to (nor cooldown-suppressed) stay eligible
        // for late pairing until their output shows up or the TTL expires.
        for (const d of diff.inputsAdded) {
            if (d.deviceId !== decision.handledInputId && !this.recentUnpairedInputs.has(d.deviceId)) {
                this.recentUnpairedInputs.set(d.deviceId, now);
            }
        }
        if (decision.handledInputId) {
            this.recentUnpairedInputs.delete(decision.handledInputId);
        }
    }
}
//#endregion
